import { Link } from "react-router-dom";
import { ArrowRight, MessageCircle } from "lucide-react";
import MarketingFooter from "../components/MarketingFooter";
import MarketingPageShell from "../components/MarketingPageShell";
import usePageMeta from "../hooks/usePageMeta";

const STEPS = [
  {
    n: "01",
    t: "Sign up",
    d: "Create your Fortiqa account in under 60 seconds — on the web or via WhatsApp. All we need is your business phone number.",
  },
  {
    n: "02",
    t: "Verify your business",
    d: "Upload your ID, a selfie, and one business document (M-Pesa till statement, KRA PIN, business permit, or similar). Tiered KYC scales to your cover.",
  },
  {
    n: "03",
    t: "Choose your cover tier",
    d: "Starter (KES 99/wk), Growth (KES 249/wk), or Business (KES 499/wk). You can switch tiers anytime from the dashboard.",
  },
  {
    n: "04",
    t: "Activate weekly payments",
    d: "Authorise weekly billing from your M-Pesa till. Premiums are pulled on a schedule you control — pause or cancel anytime.",
  },
  {
    n: "05",
    t: "Access dashboard & badge",
    d: "Your dashboard activates instantly with claims tools, CyberScore tracking, and your Fortiqa Verified Trust Badge.",
  },
];

export default function MerchantOnboarding() {
  usePageMeta({
    title: "Merchant onboarding — Fortiqa",
    description: "Five steps from signup to active cover. KYC, tier selection, weekly billing, and your Fortiqa Trust Badge.",
  });
  return (
    <div className="min-h-screen bg-[#f4f2eb] text-[#1b1d1c]" data-testid="merchant-onboarding-page">
      <MarketingPageShell
        eyebrow="Merchant onboarding"
        title="From signup to active cover in 5 steps."
        intro="No paperwork, no agents, no in-person appointments. Every step happens on your phone — most merchants finish in under 10 minutes."
        testid="merchant-onboarding"
      >
        <section className="px-6 lg:px-10 max-w-7xl mx-auto pb-12 space-y-4">
          {STEPS.map((s) => (
            <div
              key={s.n}
              className="rounded-2xl bg-white border border-[#114232]/10 p-6 sm:p-7 flex items-start gap-5 hover:border-[#114232]/30 hover:shadow-md transition-all"
            >
              <div className="font-mono text-2xl sm:text-3xl font-bold text-[#c15337] shrink-0 w-10 sm:w-14">
                {s.n}
              </div>
              <div>
                <div className="font-heading text-lg sm:text-xl font-bold text-[#114232] mb-1.5">
                  {s.t}
                </div>
                <p className="text-sm sm:text-base text-[#5c6360] leading-relaxed">{s.d}</p>
              </div>
            </div>
          ))}
        </section>

        <section className="px-6 lg:px-10 max-w-7xl mx-auto pb-24">
          <div className="bg-[#114232] rounded-3xl p-8 sm:p-10 lg:p-14 text-[#f4f2eb] flex flex-col lg:flex-row items-start lg:items-center justify-between gap-6">
            <div>
              <div className="text-[11px] font-bold uppercase tracking-[0.22em] text-[#d49a36] mb-2">
                Start now
              </div>
              <h3 className="font-heading text-2xl sm:text-3xl font-bold leading-tight">
                Onboard in 60 seconds.
              </h3>
            </div>
            <div className="flex flex-col sm:flex-row gap-3">
              <Link
                to="/signup"
                data-testid="merchant-onboarding-cta-signup"
                className="inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-full bg-[#c15337] hover:bg-[#a8472f] text-white font-semibold transition-all shadow-md hover:shadow-lg"
              >
                Create account <ArrowRight className="w-4 h-4" />
              </Link>
              <a
                href="https://wa.me/254700000000?text=Hi%20Fortiqa%2C%20I%20want%20to%20sign%20up"
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-full bg-white/10 hover:bg-white/15 text-[#f4f2eb] font-semibold transition-all border border-white/20"
              >
                <MessageCircle className="w-4 h-4 text-[#25D366]" /> On WhatsApp
              </a>
            </div>
          </div>
        </section>
      </MarketingPageShell>
      <MarketingFooter />
    </div>
  );
}
